"use client";

import { forwardRef, useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { cn } from "~/lib/utils";
import { DotsVerticalIcon } from "~/components/icons";

export interface RowActionMenuItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface RowActionMenuProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  items: RowActionMenuItem[];
  align?: "left" | "right";
}

const RowActionMenu = forwardRef<HTMLButtonElement, RowActionMenuProps>(
  ({ className, items, align = "right", ...props }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const updateMenuPosition = useCallback(() => {
      if (!buttonRef.current) return;

      const buttonRect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 168;
      const menuHeight = items.length * 32 + 16; // Approximate menu height

      let top = buttonRect.bottom + 4;
      let left = align === "right"
        ? buttonRect.right - menuWidth
        : buttonRect.left;

      // Check if menu would go off the bottom of the screen
      if (top + menuHeight > window.innerHeight) {
        top = buttonRect.top - menuHeight - 4;
      }

      // Check if menu would go off the right edge
      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 8;
      }

      // Check if menu would go off the left edge
      if (left < 8) {
        left = 8;
      }

      setMenuPosition({ top, left });
    }, [align, items.length]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          menuRef.current &&
          !menuRef.current.contains(target) &&
          buttonRef.current &&
          !buttonRef.current.contains(target)
        ) {
          setIsOpen(false);
        }
      };

      const handleScroll = () => {
        if (isOpen) {
          updateMenuPosition();
        }
      };

      const handleResize = () => {
        if (isOpen) {
          updateMenuPosition();
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScroll, true);
        window.addEventListener("resize", handleResize);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleResize);
      };
    }, [isOpen, updateMenuPosition]);

    const handleToggle = () => {
      if (!isOpen) {
        updateMenuPosition();
      }
      setIsOpen(!isOpen);
    };

    const handleItemClick = (item: RowActionMenuItem) => {
      if (!item.disabled) {
        item.onClick();
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    // Combine the forwarded ref with the internal ref
    const setRefs = (element: HTMLButtonElement | null) => {
      buttonRef.current = element;
      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    return (
      <>
        <button
          ref={setRefs}
          type="button"
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-md transition-colors cursor-pointer",
            isOpen
              ? "bg-[rgba(0,114,220,0.14)] opacity-[0.92]"
              : "bg-transparent hover:bg-[rgba(0,114,220,0.08)]",
            className
          )}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          aria-label="Row actions"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          {...props}
        >
          <DotsVerticalIcon className="text-[#003261b3]" />
        </button>

        {isOpen &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              ref={menuRef}
              role="menu"
              className="fixed z-9999 w-[168px] rounded-lg border border-[rgba(0,0,51,0.06)] bg-white p-2 shadow-lg"
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
              }}
            >
              {items.map((item, index) => (
                <button
                  key={index}
                  type="button"
                  role="menuitem"
                  className={cn(
                    "flex h-8 w-full items-center rounded px-3 text-left text-sm text-[#1c2024] transition-colors",
                    item.disabled
                      ? "cursor-not-allowed opacity-50"
                      : "hover:bg-[#f9f9fb] cursor-pointer"
                  )}
                  onClick={() => handleItemClick(item)}
                  disabled={item.disabled}
                  tabIndex={0}
                >
                  {item.label}
                </button>
              ))}
            </div>,
            document.body
          )}
      </>
    );
  }
);

RowActionMenu.displayName = "RowActionMenu";

export { RowActionMenu };
